import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Profile } from "@/hooks/designer-grid/types";
import { ContentPagination } from "@/v2/services/types/serviceInterfaces";
import { ALL_COUNTRIES } from "@/data/countries";

interface ProfileResponse {
  profiles: any[];
  total_count: number;
}

interface RPCParams {
  disciplines: string[] | null;
  locations: string[] | null;
  limit_count: number;
  offset_value: number;
  seed: string;
}

// Hook for profile filters
export const useProfileFilters = () => {
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState<string[]>([]);

  const handleLocationToggle = (location: string) => {
    setSelectedLocations((prev) =>
      prev.includes(location)
        ? prev.filter((loc) => loc !== location)
        : [...prev, location]
    );
  };

  const handleDisciplineToggle = (discipline: string) => {
    setSelectedDisciplines((prev) =>
      prev.includes(discipline)
        ? prev.filter((disc) => disc !== discipline)
        : [...prev, discipline]
    );
  };

  const handleFilterReset = () => {
    setSelectedLocations([]);
    setSelectedDisciplines([]);
  };

  return {
    selectedLocations,
    selectedDisciplines,
    handleLocationToggle,
    handleDisciplineToggle,
    handleFilterReset,
  };
};

/**
 * Hook to fetch all profiles with filtering and pagination
 * Featured profiles will appear first in the results
 */
export const useAllProfiles = () => {
  const queryClient = useQueryClient();
  const {
    selectedLocations,
    selectedDisciplines,
    handleLocationToggle,
    handleDisciplineToggle,
    handleFilterReset,
  } = useProfileFilters();
  const [page, setPage] = useState(1);
  const limit = 12;

  // Wrap filter toggle handlers to reset pagination
  const wrappedHandleLocationToggle = (location: string) => {
    setPage(1);
    handleLocationToggle(location);
  };

  const wrappedHandleDisciplineToggle = (discipline: string) => {
    setPage(1);
    handleDisciplineToggle(discipline);
  };

  // Map selectedLocations from ids to labels
  const getCountryLabel = (id) =>
    ALL_COUNTRIES.find((c) => c.id === id)?.label || id;
  const selectedLocationLabels = selectedLocations.map(getCountryLabel);

  const result = useQuery({
    queryKey: [
      "all-profiles",
      {
        disciplines: selectedDisciplines,
        locations: selectedLocationLabels,
        limit,
        page,
      },
    ],
    queryFn: async () => {
      // Construct query parameters
      const params: RPCParams = {
        disciplines: selectedDisciplines.length ? selectedDisciplines : null,
        locations: selectedLocationLabels.length
          ? selectedLocationLabels
          : null,
        limit_count: limit,
        offset_value: (page - 1) * limit,
        seed: localStorage.getItem("userSessionId") || "",
      };

      // Call the RPC function to get profiles with offset-based pagination
      const { data, error } = await supabase.rpc(
        "get_profiles_with_offset",
        params
      );

      if (error) throw error;

      // Cast the response to our expected type
      const response = data as unknown as ProfileResponse;

      // Check if data exists and has the expected structure
      if (
        !response ||
        !response.profiles ||
        !Array.isArray(response.profiles)
      ) {
        return { profiles: [], totalCount: 0, pagination: null };
      }

      // Process profiles to ensure they match the Profile type
      const profiles = response.profiles.map((profile: any) => ({
        id: profile.id,
        username: profile.username,
        username_slug: profile.username_slug,
        profession: profile.profession,
        location: profile.location,
        avatar_url: profile.avatar_url,
        is_admin: profile.is_admin || false,
        is_featured: profile.is_featured || false,
        skills: profile.skills || [],
        view_count: profile.view_count || 0,
        like_count: profile.like_count || 0,
        is_liked: profile.is_liked || false,
        display_name: profile.display_name || profile.username,
        hire_me_enabled: profile.hire_me_enabled || false,
        trending_score: profile.trending_score || 0,
        is_trending: (profile.trending_score || 0) > 10,
      }));

      const totalCount =
        typeof response.total_count === "number" ? response.total_count : 0;
      const totalPages = Math.ceil(totalCount / limit);
      const currentPage = page;
      const hasNextPage = currentPage < totalPages;
      const hasPrevPage = currentPage > 1;

      // Return both the profiles array and pagination info
      return {
        profiles,
        totalCount,
        pagination: {
          currentPage,
          totalPages,
          hasNextPage,
          hasPrevPage,
          totalCount,
        },
      };
    },
  });

  // Function to handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return {
    ...result,
    handlePageChange,
    filters: {
      selectedLocations,
      selectedDisciplines,
      handleLocationToggle: wrappedHandleLocationToggle,
      handleDisciplineToggle: wrappedHandleDisciplineToggle,
      handleFilterReset,
    },
  };
};
